import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { crisisService, CrisisAlert, CrisisWeeklyTrend } from '../services/crisisService';
import { questionnaireService } from '../services/questionnaireService';
import { Questionnaire } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CrisisMonitorPage: React.FC = () => {
  const navigate = useNavigate();

  // —— 问卷选择（必选，否则不加载任何数据） ——
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [questionnairesLoading, setQuestionnairesLoading] = useState(true);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(null);

  // —— 数据 ——
  const [alerts, setAlerts] = useState<CrisisAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // —— 筛选（学校 / 班级 / 指标） ——
  const [filterSchool, setFilterSchool] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterIndicator, setFilterIndicator] = useState('');

  // —— 分页 ——
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // —— 趋势 ——
  const [trendWeeks, setTrendWeeks] = useState(8);
  const [trendData, setTrendData] = useState<CrisisWeeklyTrend | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);

  // 初始加载问卷列表（取所有 — 一次拿大一点 size 够用）
  useEffect(() => {
    (async () => {
      try {
        setQuestionnairesLoading(true);
        const res = await questionnaireService.getQuestionnaires({ page: 0, size: 200 });
        setQuestionnaires(res.content || []);
      } catch (err) {
        console.error('Failed to load questionnaire list', err);
      } finally {
        setQuestionnairesLoading(false);
      }
    })();
  }, []);

  // 选中问卷 → 加载预警 + 趋势
  useEffect(() => {
    if (selectedQuestionnaireId == null) {
      setAlerts([]);
      setTrendData(null);
      return;
    }
    fetchData(selectedQuestionnaireId);
    fetchTrend(selectedQuestionnaireId, trendWeeks);
    // 切换问卷重置筛选
    setFilterSchool('');
    setFilterClass('');
    setFilterIndicator('');
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionnaireId]);

  // 趋势周数变化 → 重新拉趋势（不重拉预警表）
  useEffect(() => {
    if (selectedQuestionnaireId == null) return;
    fetchTrend(selectedQuestionnaireId, trendWeeks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendWeeks]);

  const fetchData = async (qid: number) => {
    try {
      setLoading(true);
      setError('');
      const data = await crisisService.getAllAlerts(qid);
      setAlerts(data);
    } catch (err: any) {
      setError(err.response?.data?.message || '加载预警数据失败');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrend = async (qid: number, weeks: number) => {
    try {
      setTrendLoading(true);
      const data = await crisisService.getWeeklyTrend(weeks, qid);
      setTrendData(data);
    } catch (err) {
      console.error('Failed to load trend data', err);
      setTrendData(null);
    } finally {
      setTrendLoading(false);
    }
  };

  const TREND_COLORS = ['#4DA1A9', '#4b5e8d', '#5f51b1', '#ac3434', '#d97706'];

  // 筛选选项（基于当前问卷预警数据动态生成）
  const schoolOptions = useMemo(
    () => Array.from(new Set(alerts.map(a => a.schoolName).filter((s): s is string => !!s))).sort(),
    [alerts]
  );
  const classOptions = useMemo(() => {
    const filteredByScope = filterSchool ? alerts.filter(a => a.schoolName === filterSchool) : alerts;
    return Array.from(new Set(filteredByScope.map(a => a.className).filter((c): c is string => !!c))).sort();
  }, [alerts, filterSchool]);
  const indicatorOptions = useMemo(
    () => Array.from(new Set(alerts.map(a => a.indicatorName).filter((n): n is string => !!n))).sort(),
    [alerts]
  );

  // 学校变化时若已选班级不在新列表中则清空
  useEffect(() => {
    if (filterClass && !classOptions.includes(filterClass)) setFilterClass('');
  }, [classOptions, filterClass]);

  const filteredAlerts = alerts.filter(a => {
    if (filterSchool && a.schoolName !== filterSchool) return false;
    if (filterClass && a.className !== filterClass) return false;
    if (filterIndicator && a.indicatorName !== filterIndicator) return false;
    return true;
  });

  const uniqueStudents = new Set(filteredAlerts.map(a => a.studentId)).size;

  // Stats - use conclusionTitle to classify alerts
  const criticalCount = filteredAlerts.filter(a => a.conclusionTitle?.includes('严重') || a.conclusionTitle?.includes('危')).length;
  const warningCount = filteredAlerts.length - criticalCount;

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / pageSize));
  const paginatedAlerts = filteredAlerts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterSchool, filterClass, filterIndicator]);

  // Determine if an alert is critical level
  const isCritical = (alert: CrisisAlert) => {
    return alert.conclusionTitle?.includes('严重') || alert.conclusionTitle?.includes('危');
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.charAt(0);
  };

  // Get severity tag info based on conclusion
  const getSeverityInfo = (alert: CrisisAlert) => {
    if (isCritical(alert)) {
      return {
        tagBg: 'bg-error-container/10',
        tagText: 'text-error',
        tagLabel: '严重',
        dotClass: 'w-3 h-3 rounded-full bg-error animate-pulse shadow-[0_0_8px_rgba(172,52,52,0.5)]',
        levelText: 'text-error',
        actionBg: 'bg-error-container',
        actionText: 'text-on-error-container',
        actionLabel: '立即干预',
        avatarBg: 'bg-error-container/20',
        avatarText: 'text-error',
        scoreText: 'text-error',
      };
    }
    return {
      tagBg: 'bg-orange-50',
      tagText: 'text-orange-600',
      tagLabel: '警告',
      dotClass: 'w-3 h-3 rounded-full bg-orange-400',
      levelText: 'text-orange-600',
      actionBg: 'bg-orange-50',
      actionText: 'text-orange-600',
      actionLabel: '需要关注',
      avatarBg: 'bg-orange-100',
      avatarText: 'text-orange-600',
      scoreText: 'text-orange-500',
    };
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const selectedQuestionnaire = questionnaires.find(q => q.id === selectedQuestionnaireId);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-extrabold text-on-surface tracking-tight font-headline">预警监控列表</h1>
              {selectedQuestionnaireId != null && (
                <span className="px-3 py-1 rounded-xl text-xs font-medium bg-error-100 text-error font-label">
                  {filteredAlerts.length} 条预警
                </span>
              )}
            </div>
            <p className="text-on-surface-variant mt-2 max-w-2xl font-body">
              {selectedQuestionnaireId == null
                ? '请先选择问卷以查看其触发预警的学生'
                : `当前问卷共涉及 ${uniqueStudents} 名预警学生`}
            </p>
          </div>
        </div>
      </div>

      {/* Questionnaire Selector */}
      <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm mb-6">
        <label className="block text-xs font-bold uppercase tracking-widest text-primary font-label mb-2">
          选择问卷 <span className="text-error">*</span>
        </label>
        <select
          value={selectedQuestionnaireId ?? ''}
          onChange={e => setSelectedQuestionnaireId(e.target.value ? Number(e.target.value) : null)}
          disabled={questionnairesLoading}
          className="w-full px-4 py-3 bg-surface-container-low rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 font-body"
        >
          <option value="">{questionnairesLoading ? '加载问卷中…' : '— 请选择问卷 —'}</option>
          {questionnaires.map(q => (
            <option key={q.id} value={q.id}>
              {q.title}
              {q.questionnaireCode ? ` (${q.questionnaireCode})` : ''}
            </option>
          ))}
        </select>
        {selectedQuestionnaire && (
          <p className="text-xs text-on-surface-variant mt-2 font-body">
            已选：<span className="font-semibold">{selectedQuestionnaire.title}</span>
          </p>
        )}
      </div>

      {selectedQuestionnaireId == null ? (
        <div className="bg-surface-container-lowest rounded-3xl p-16 text-center shadow-sm">
          <span className="material-symbols-outlined text-surface-dim mb-4" style={{ fontSize: '64px' }}>quiz</span>
          <p className="text-on-surface-variant font-body">请先在上方选择一份问卷</p>
        </div>
      ) : (
        <>
          {/* Stats + Chart Row */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
            {/* Chart - 8 cols */}
            <div className="lg:col-span-8 bg-surface-container-lowest rounded-3xl p-8 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-on-surface font-headline">每周预警人数趋势</h3>
                <div className="flex items-center gap-4">
                  {trendData && trendData.indicators.length > 0 && (
                    <div className="flex gap-4 text-xs font-semibold tracking-wider flex-wrap">
                      {trendData.indicators.map((name, i) => (
                        <span key={name} className="flex items-center gap-2" style={{ color: TREND_COLORS[i % TREND_COLORS.length] }}>
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: TREND_COLORS[i % TREND_COLORS.length] }}></span>
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                  <select
                    value={trendWeeks}
                    onChange={e => setTrendWeeks(Number(e.target.value))}
                    className="px-3 py-1.5 bg-surface-container-low rounded-lg text-xs text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 font-label"
                  >
                    <option value={4}>最近4周</option>
                    <option value={8}>最近8周</option>
                    <option value={12}>最近12周</option>
                  </select>
                </div>
              </div>
              <div className="h-64">
                {trendLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : !trendData || trendData.indicators.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-on-surface-variant">
                    <span className="material-symbols-outlined mb-2" style={{ fontSize: '36px' }}>show_chart</span>
                    <p className="text-sm font-body">暂无预警趋势数据</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData.weeks.map(w => ({ weekLabel: w.weekLabel, ...w.counts }))}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e9ed" />
                      <XAxis dataKey="weekLabel" tick={{ fontSize: 11, fill: '#586064' }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#586064' }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 30px rgba(44,52,55,0.1)', fontSize: '12px' }}
                        labelStyle={{ fontWeight: 700, marginBottom: '4px' }}
                      />
                      {trendData.indicators.map((name, i) => (
                        <Line key={name} type="monotone" dataKey={name} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Stats cards - 4 cols */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              <div className="bg-primary/5 p-6 rounded-3xl relative overflow-hidden flex-1">
                <div className="relative z-10">
                  <p className="text-xs font-bold uppercase tracking-widest text-primary font-label">活跃预警数</p>
                  <h4 className="text-4xl font-black text-primary mt-1 font-headline">{filteredAlerts.length}</h4>
                  <p className="text-xs text-primary-dim mt-2 font-medium font-label">
                    严重 {criticalCount} · 警告 {warningCount}
                  </p>
                </div>
                <div className="absolute -right-4 -bottom-4 opacity-10">
                  <span className="material-symbols-outlined" style={{ fontSize: '128px' }}>crisis_alert</span>
                </div>
              </div>
              <div className="bg-tertiary/5 p-6 rounded-3xl relative overflow-hidden flex-1">
                <div className="relative z-10">
                  <p className="text-xs font-bold uppercase tracking-widest text-tertiary font-label">待处理干预</p>
                  <h4 className="text-4xl font-black text-tertiary mt-1 font-headline">{uniqueStudents}</h4>
                  <p className="text-xs text-tertiary-dim mt-2 font-medium font-label">
                    {criticalCount} 个高优先级案例
                  </p>
                </div>
                <div className="absolute -right-4 -bottom-4 opacity-10">
                  <span className="material-symbols-outlined" style={{ fontSize: '128px' }}>medical_services</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          {!loading && (
            <div className="flex flex-wrap gap-3 mb-6">
              <select
                value={filterSchool}
                onChange={e => setFilterSchool(e.target.value)}
                className="px-4 py-2.5 bg-surface-container-low rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 font-body"
              >
                <option value="">全部学校</option>
                {schoolOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={filterClass}
                onChange={e => setFilterClass(e.target.value)}
                disabled={classOptions.length === 0}
                className="px-4 py-2.5 bg-surface-container-low rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 font-body disabled:opacity-50"
              >
                <option value="">全部班级</option>
                {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={filterIndicator}
                onChange={e => setFilterIndicator(e.target.value)}
                disabled={indicatorOptions.length === 0}
                className="px-4 py-2.5 bg-surface-container-low rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 font-body disabled:opacity-50"
              >
                <option value="">全部指标</option>
                {indicatorOptions.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              {(filterSchool || filterClass || filterIndicator) && (
                <button
                  onClick={() => { setFilterSchool(''); setFilterClass(''); setFilterIndicator(''); }}
                  className="px-4 py-2.5 text-sm text-on-surface-variant hover:text-on-surface font-body"
                >
                  清空筛选
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="bg-error-50 text-error px-5 py-4 rounded-2xl mb-6 font-body text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-20">
              <span className="material-symbols-outlined text-surface-dim mb-4" style={{ fontSize: '48px' }}>shield</span>
              <p className="text-on-surface-variant font-body">暂无预警记录</p>
            </div>
          ) : (
            /* Alert Table */
            <section className="bg-surface-container-lowest rounded-[2rem] overflow-hidden shadow-sm">
              {/* Table Sub-header */}
              <div className="px-8 py-6 flex justify-between items-center bg-slate-50/50">
                <h3 className="text-lg font-bold text-on-surface font-headline">预警详情记录</h3>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-slate-400">
                    显示前 {Math.min(filteredAlerts.length, pageSize * totalPages)} 条关键记录
                  </span>
                </div>
              </div>

              {/* Table */}
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                    <th className="px-8 py-4">学生信息</th>
                    <th className="px-8 py-4">学校/班级</th>
                    <th className="px-8 py-4 text-center">指标分数</th>
                    <th className="px-8 py-4">预警等级</th>
                    <th className="px-8 py-4">需要行动</th>
                    <th className="px-8 py-4 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedAlerts.map((alert, idx) => {
                    const severity = getSeverityInfo(alert);
                    return (
                      <tr
                        key={idx}
                        className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/user-search/students/${alert.studentId}`)}
                      >
                        {/* Student Info */}
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${severity.avatarBg} flex items-center justify-center ${severity.avatarText} font-bold text-xs flex-shrink-0`}>
                              {getInitials(alert.studentName)}
                            </div>
                            <div>
                              <p className="font-bold text-on-surface">{alert.studentName}</p>
                              <p className="text-xs text-slate-400">{alert.studentNumber || alert.idCardNumber}</p>
                            </div>
                          </div>
                        </td>

                        {/* School/Class */}
                        <td className="px-8 py-6">
                          <p className="text-sm font-medium text-on-surface">{alert.schoolName || '-'}</p>
                          {alert.className && (
                            <p className="text-xs text-slate-400">{alert.grade} {alert.className}</p>
                          )}
                        </td>

                        {/* Indicator Score */}
                        <td className="px-8 py-6 text-center">
                          <div className="inline-flex items-center gap-2">
                            <span className={`text-lg font-black ${severity.scoreText}`}>{alert.score}</span>
                            <span className={`text-[10px] ${severity.tagBg} ${severity.tagText} px-1.5 py-0.5 rounded uppercase font-bold`}>
                              {severity.tagLabel}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">{alert.indicatorName}</p>
                        </td>

                        {/* Warning Level */}
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-2">
                            <div className={severity.dotClass}></div>
                            <span className={`text-sm font-bold ${severity.levelText}`}>
                              {alert.conclusionTitle}
                            </span>
                          </div>
                        </td>

                        {/* Action Needed */}
                        <td className="px-8 py-6">
                          <span className={`px-3 py-1 ${severity.actionBg} ${severity.actionText} text-[11px] font-bold rounded-lg uppercase`}>
                            {severity.actionLabel}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-8 py-6 text-right">
                          <button
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/user-search/students/${alert.studentId}`);
                            }}
                          >
                            <span className="material-symbols-outlined text-slate-400" style={{ fontSize: '20px' }}>visibility</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination Footer */}
              <div className="px-8 py-6 flex justify-between items-center bg-slate-50/30">
                <div className="flex gap-2">
                  {getPageNumbers().map((page, idx) =>
                    typeof page === 'string' ? (
                      <span key={`ellipsis-${idx}`} className="flex items-center px-2">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-all ${
                          currentPage === page
                            ? 'bg-white shadow-sm text-primary'
                            : 'hover:bg-slate-200'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <p className="text-sm text-slate-400">
                  显示 {(currentPage - 1) * pageSize + 1} 到 {Math.min(currentPage * pageSize, filteredAlerts.length)} 共 {filteredAlerts.length} 条预警
                </p>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default CrisisMonitorPage;
